import * as React from 'react';

import { IAccordionData } from '../types';
import { Accordion } from '../components/Accordion';
import { Image } from '../components/Image';
import create2 from '../screenshots/Creating/create2.jpg';
import shortlink1 from '../screenshots/Creating/shortlink1.jpg';
import shortlink2 from '../screenshots/Creating/shortlink2.jpg';
import shortlink3 from '../screenshots/Creating/shortlink3.jpg';
import shortlink4 from '../screenshots/Creating/shortlink4.jpg';
import shortlinkbulk1 from '../screenshots/Creating/image017.png';
import shortlinkbulk2 from '../screenshots/Creating/image019.png';
import qr1 from '../screenshots/Creating/qr1.jpg';
import bulk1 from '../screenshots/Creating/bulk1.jpg';
import bulk2 from '../screenshots/Creating/bulk2.png';
import bulk3 from '../screenshots/Creating/bulk3.jpg';

const sections: IAccordionData[] = [
  {
    question: 'How can I create a trackinglink?',
    answer: () => (
      <ol>
        <li>
          <p>Navigate to „Create New Links“ &rarr; „Individual Link“:</p>
        </li>
        <li>
          <p>
            Select or add required campaign values in the „Create New Link“
            interface:
          </p>
          <Image src={create2} />
        </li>
        <li>
          <p>
            After successfuly filling out all required campaign values, click on
            „Create link“ button.
          </p>
        </li>
        <li>
          <p>
            If you want to remove all values at once and start again, click on
            the „Reset“ button.
          </p>
        </li>
      </ol>
    ),
  },
  {
    question: 'How can I create a shortlink?',
    hasSubquestions: true,
    answer: index => (
      <>
        <Accordion
          isSub={true}
          title={`${index}.1 Via the interface`}
          content={
            <>
              <p>You may create shortlinks in two locations:</p>
              <p>
                a) „Create New Links“ &rarr; „Individual Link“ page directly
                after having created a single trackinglink:
              </p>
              <Image src={shortlink1} />
              <p>
                b) „Existing Links“ page for links that have already been
                created:
              </p>
              <Image src={shortlink2} />
              <ol>
                <li>
                  <p>Click on the „Create shortlink“ button.</p>
                </li>
                <li>
                  <p className="mb-2">
                    a) Select „Generate automatically“ in order to create a
                    shortlink with an automatically generated short ID, such as:{' '}
                    <code className="font-mono text-base">
                      http://fel.dm/lHrLrqzI
                    </code>
                    .
                  </p>
                  <p>
                    b) Select „Create Semantic URL“, in order to create a
                    shortlink with a short text that you can type in yourself in
                    the free text field below, such as:{' '}
                    <code className="font-mono text-base">
                      http://fel.dm/datacroft-shortlink
                    </code>
                    .
                    <br />
                    You will see a preview of your shortlink below the text
                    field.
                  </p>
                  <Image src={shortlink3} />
                </li>
                <li>
                  <p>
                    Click on the „Generate shortlink“ button to create the
                    shortlink.
                  </p>
                  <Image src={shortlink4} />
                </li>
              </ol>
            </>
          }
        />
        <Accordion
          isSub={true}
          title={`${index}.2 Via the Bulk Upload feature`}
          content={
            <>
              <p className="mb-2">
                The fields which refer the shortlinks can be found at the end of
                the template dropdowns.
              </p>
              <p>
                „Shortener“: Select „No“ or leave the field empty if you do not
                want to create a shortlink.
              </p>
              <Image src={shortlinkbulk1} />
              <p>
                a) Select „Automatic“ if you want to generate a shortlink with a
                random short ID.
              </p>
              <p>
                b) Select „Create Semantic ID“ if you want to create a shortlink
                with a short phrase or text. You will also have to fill out the
                phrase or text you want to use in the shortlink in the next
                field „Semantic ID“. If you do not fill out this field, it will
                be treated as an error.
              </p>
              <Image src={shortlinkbulk2} />
            </>
          }
        />
      </>
    ),
  },
  {
    question: 'How can I create a QR code?',
    answer: () => (
      <>
        <p>
          QR codes are created whenever a trackinglink or shortlink is created.
        </p>
        <p>
          To use a QR code, hover over the QR code icon and copy or save it:
        </p>
        <Image src={qr1} />
      </>
    ),
  },
  {
    question: 'What is the Bulk Upload feature and how can I use it?',
    answer: () => (
      <>
        <p>
          The Bulk Upload feature was created in order to create a large number
          of trackinglinks and shortlinks in one go, for example for large SEA
          campaigns.
        </p>
        <ol>
          <li>
            <p>
              In order to use the feature, navigate to „Create New Links“ &rarr;
              „Bulk Upload“:
            </p>
          </li>
          <li>
            <p>Start by selecting the required (market) profile.</p>
          </li>
          <li>
            <p>
              Now choose a template. If there are no custom templates
              configured, you will only see the default template.
            </p>
          </li>
          <li>
            <p>
              Click on „Download template“ button to download the Excel template
              to your machine.
            </p>
            <Image src={bulk1} />
          </li>
          <li>
            <p>Open the file and start filling out all required values.</p>
            <p>
              <em>(Note: screenshot shows generic demo setup)</em>
            </p>
            <Image src={bulk2} />
          </li>
          <li>
            <p>
              After all values have been filled out, save the template and
              upload it on the „Bulk Upload“ page:
            </p>
            <Image src={bulk3} />
            <p>
              You can add other email-adresses than your own in the
              „Confirmation mail CC“ field.
            </p>
            <p>Click on the „Upload file“ button to upload the file.</p>
            <p>
              You will receive a notification mail once the trackinglinks have
              started to be generated in the tool. You will receive a second
              mail once the trackinglinks have been finished and the template is
              filled out.
            </p>
            <p>
              Trackinglinks which have been created successfully will be marked
              in <strong className="text-green">GREEN</strong>. Duplicate links
              will be marked in <strong className="text-yellow">ORANGE</strong>.
              If trackinglinks could not be created because of errors in the
              selected values or because of missing values, these will be marked
              in <strong className="text-red">RED</strong> and a short error
              message will be displayed on the right of the template values.
            </p>
          </li>
        </ol>
      </>
    ),
  },
];

export const Creating = ({ index }: { index: number }) => (
  <>
    <h2 className="mt-12 mb-6">{index}. Creating tracking links</h2>
    {sections.map(({ question, answer, hasSubquestions }, subindex) => (
      <Accordion
        title={`${index}.${subindex + 1}. ${question}`}
        content={answer(`${index}.${subindex + 1}`)}
        key={subindex}
        hasSubquestions={hasSubquestions}
      />
    ))}
  </>
);



// WEBPACK FOOTER //
// ./src/views/Tutorial/sections/Creating.tsx